import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BaseService } from '../base/base-service';
import { API_CONSTANTS } from '../base/api-constants';

export type ProfileRefreshStatusName = 'Current' | 'Due' | 'Skipped' | 'InProgress';

export interface ProfileRefreshStatus {
  status: ProfileRefreshStatusName;
  dueAtUtc?: string;
  lastRefreshAtUtc?: string;
  lastCompletedStep?: number;
  totalSteps: number;
  canApply: boolean;
  resumeUrl?: string;
  daysOverdue?: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProfileRefreshService extends BaseService {
  private readonly statusSubject = new BehaviorSubject<ProfileRefreshStatus | null>(null);
  readonly status$ = this.statusSubject.asObservable();

  constructor(private httpClient: HttpClient) {
    super(httpClient);
  }

  get snapshot(): ProfileRefreshStatus | null {
    return this.statusSubject.value;
  }

  isInRefreshFlow(): boolean {
    const status = this.statusSubject.value?.status;
    return status === 'InProgress' || status === 'Skipped';
  }

  canApply(): boolean {
    return this.statusSubject.value?.canApply !== false;
  }

  loadStatus(): Observable<any> {
    return this.get(API_CONSTANTS.Candidate.ProfileRefresh.Status).pipe(
      tap((res: any) => {
        if (res?.data) {
          this.statusSubject.next(res.data as ProfileRefreshStatus);
        }
      })
    );
  }

  startRefresh(): Observable<any> {
    return this.post(API_CONSTANTS.Candidate.ProfileRefresh.Start, {}).pipe(
      tap((res: any) => {
        if (res?.data) {
          this.statusSubject.next(res.data as ProfileRefreshStatus);
        }
      })
    );
  }

  skipRefresh(): Observable<any> {
    return this.post(API_CONSTANTS.Candidate.ProfileRefresh.Skip, {}).pipe(
      tap((res: any) => {
        if (res?.data) {
          this.statusSubject.next(res.data as ProfileRefreshStatus);
        }
      })
    );
  }

  updateStep(step: number): Observable<any> {
    return this.post(`${API_CONSTANTS.Candidate.ProfileRefresh.Step}/${step}`, {}).pipe(
      tap((res: any) => {
        if (res?.data) {
          this.statusSubject.next(res.data as ProfileRefreshStatus);
        }
      })
    );
  }

  completeRefresh(): Observable<any> {
    return this.post(API_CONSTANTS.Candidate.ProfileRefresh.Complete, {}).pipe(
      tap((res: any) => {
        if (res?.data) {
          this.statusSubject.next(res.data as ProfileRefreshStatus);
        }
      })
    );
  }

  shouldShowDueModal(): boolean {
    return this.statusSubject.value?.status === 'Due';
  }

  shouldShowBanner(): boolean {
    const status = this.statusSubject.value?.status;
    return status === 'Skipped' || status === 'InProgress';
  }
}
